// Matter detail.
//
// Two render paths:
//   1. universal — when matter.practiceAreaTemplateId resolves to a
//      PracticeAreaTemplate from /api/practice-areas/:id, render the
//      config-driven `MatterDashboard` (template.ui.widgets[]).
//   2. legacy — fall back to PracticeAreaDispatcher → AsylumWorkspace /
//      CriminalWorkspace / RegistryWorkspace. This path stays in place
//      for cases that haven't been classified yet.
//
// Multi-matter chip strip when client has 2+ active matters.

import { Link, useParams } from "react-router-dom";
import { mattersForClient } from "../../data/matters";
import {
  OTHER_AREA,
  paStyle,
  practiceAreaById,
  practiceAreaLabel,
} from "../../data/practice-areas";
import { useMatter } from "../../hooks/useMatter";
import { usePracticeAreaTemplate } from "../../hooks/usePracticeAreaTemplate";
import { DuplicateNotice } from "./DuplicateNotice";
import { SignalNotice } from "./SignalNotice";
import { ReviewBanner } from "./ReviewBanner";
import { MatterDashboard } from "./MatterDashboard";
import { PracticeAreaDispatcher } from "./PracticeAreaDispatcher";
import { resolveTemplateId } from "./paTemplateMap";
import { FixtureNotice } from "../chrome/FixtureNotice";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import { SEP } from "../chrome/SepList";
import { PENDING, usePageTrail } from "../chrome/trail";

const FIXTURE_NOTICE: Record<string, string> = {
  missing: "This matter is not in the firm database - showing prototype data.",
  unreachable: "Could not reach the server - showing prototype data.",
};

export function MatterView() {
  const { eID } = useParams<{ eID: string }>();
  const { data: matterWithClient, isLoading, isError } = useMatter(eID);
  const area = matterWithClient?.practiceArea ?? "";
  const pa = practiceAreaById(area);
  const eIdStep = { label: matterWithClient?.eID ?? eID ?? "Matter" };
  usePageTrail(
    matterWithClient
      ? [
          // The Other bucket has no /practice page; the rail sends it to the list too.
          pa
            ? { label: pa.label, to: `/practice/${area}` }
            : {
                label: practiceAreaLabel(area),
                to: area === OTHER_AREA.id ? OTHER_AREA.to : undefined,
              },
          {
            label: matterWithClient.client ? matterWithClient.client.name : "(unknown)",
            to: `/client/${matterWithClient.clientId}`,
          },
          eIdStep,
        ]
      : isLoading
        ? [PENDING, PENDING, eIdStep]
        : [eIdStep],
  );

  // Most live matters carry no stamped template id; the resolver picks one
  // by case type.
  const templateId = resolveTemplateId(matterWithClient ?? null);
  const { data: template } = usePracticeAreaTemplate(templateId);

  if (isLoading) {
    return (
      <div className="main">
        <div style={{ padding: 24 }}>
          <SkeletonGroup label="Loading matter">
            <Skeleton width="40%" height={22} />
            <Skeleton width="65%" height={12} />
            <div style={{ height: 8 }} />
            <Skeleton width="100%" height={140} radius={10} />
            <div style={{ height: 8 }} />
            <Skeleton width="100%" height={120} radius={10} />
          </SkeletonGroup>
        </div>
      </div>
    );
  }

  if (isError) {
    // The matter's existence is unknown when the API is down - saying
    // "not found" here would be a false claim about the firm's data.
    return (
      <div className="detail-body">
        <div className="cc-card" style={{ padding: 18, color: "var(--warn-600)" }}>
          Could not reach the server - matter <code>{eID}</code> is unavailable.
        </div>
      </div>
    );
  }

  if (!matterWithClient) {
    return (
      <div className="detail-body">
        <div className="cc-card" style={{ padding: 18 }}>
          <p style={{ color: "var(--muted)", fontSize: 12 }}>
            Matter <code>{eID}</code> not found.
          </p>
        </div>
      </div>
    );
  }

  const matter = matterWithClient;
  const client = matterWithClient.client;
  const allMatters = mattersForClient(matter.clientId);

  return (
    <div className="main" style={paStyle(matter.practiceArea)}>
      <div className="detail-body">
        {matter.fixtures && <FixtureNotice>{FIXTURE_NOTICE[matter.fixtures]}</FixtureNotice>}
        {matter.needsReview && matter.mongoId && (
          <ReviewBanner caseId={matter.mongoId} eID={matter.eID} />
        )}
        {matter.mongoId && <DuplicateNotice caseId={matter.mongoId} eID={matter.eID} />}
        {matter.mongoId && <SignalNotice caseId={matter.mongoId} />}

        {allMatters.length > 1 && (
          <div className="cc-card" style={{ padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6 }}>
              <b>{client?.name}</b> · {allMatters.length} matters under this client
            </div>
            <div className="v2-matter-chips">
              {allMatters.map((m) => {
                const mpa = practiceAreaById(m.practiceArea);
                return (
                  <Link
                    key={m.eID}
                    to={`/matter/${m.eID}`}
                    className={"v2-matter-chip" + (m.eID === eID ? " active" : "")}
                    title={m.aiSummary?.headline}
                    style={{ textDecoration: "none" }}
                  >
                    <span
                      className="pa-dot"
                      style={{ background: mpa ? mpa.accent : "var(--muted)" }}
                    />
                    <span>{mpa ? mpa.shortLabel : practiceAreaLabel(m.practiceArea)}</span>
                    <span className="eid">{m.eID}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {template ? (
          <MatterDashboard
            matter={matter}
            client={client}
            pa={pa}
            template={template}
            templateId={templateId}
          />
        ) : (
          // Legacy per-area workspaces. Kept in place for matters whose
          // resolved template id has no backend YAML (e.g. unsupported PA).
          // The legacy workspaces themselves use MatterHero so per-PA
          // differentiation still shows.
          <PracticeAreaDispatcher matter={matter} pa={pa} client={client} templateId={templateId} />
        )}

        <div className="cc-detail-footer">
          <span>
            {matter.matter?.filedDate
              ? `Filed${SEP}${String(matter.matter.filedDate).slice(0, 10)}`
              : `Opened${SEP}${(matter.opened || "").slice(0, 10)}`}
          </span>
          <span className="sep">·</span>
          <span>
            {matter.deadlines?.length ?? 0} deadline
            {(matter.deadlines?.length ?? 0) === 1 ? "" : "s"}
          </span>
          <span className="sep">·</span>
          <span>
            {matter.documents?.length ?? 0} document
            {(matter.documents?.length ?? 0) === 1 ? "" : "s"}
          </span>
          <span className="sep">·</span>
          <code>{matter.eID}</code>
        </div>
      </div>
    </div>
  );
}
